import { defineStore } from 'pinia';

import {
  DTypeDTO,
  MTypeDTO,
  ShearForceTypeDTO,
  ShortElementTypeDTO,
  SMVariantDTO,
  SteelSMTypeDTO,
  SteelSQTypeDTO,
  UpdateHeatFlowDto,
} from '~/api/webservice';
import {
  FIRE_PROTECTION_OPTIONS,
  HEIGHT_OPTIONS,
  HeightOffsetTypeProductDto,
  InstallationSetup,
  ISOPRO,
  ISOPROFireProtection,
  ISOPROProductDType,
  ISOPROProductHeightOffsetVariantType,
  ISOPROProductMType,
  ISOPROProductShearForceType,
  ISOPROProductShortElementType,
  ISOPROType,
  LENGTH_OPTIONS,
  LOAD_BEARING_CAPACITY_OPTIONS,
  SHEAR_LOAD_FORCE,
  TYPE_OPTIONS,
  VARIANT_OPTIONS,
  WallStructure,
} from '~/models';
import {
  ISOPROProductSMType,
  ISOPROProductSMVariantType,
  ISOPROProductSQType,
} from '~/models/product/product.steelType';
import { useThresholdsStore, useWallStructuresStore } from '~/store';
import { deepMerge } from '~/utils/objects';

import { productsAPI } from '../APIConnection';
import { AdaptedISOPROType, CategoriesList, VariantGroupKey } from '../views/DataTablesView/models';

export interface FilterValues {
  type: keyof typeof TYPE_OPTIONS;
  loadBearingCapacity: keyof typeof LOAD_BEARING_CAPACITY_OPTIONS;
  height: keyof typeof HEIGHT_OPTIONS;
  shearForce: keyof typeof SHEAR_LOAD_FORCE;
  fireProtection: keyof typeof FIRE_PROTECTION_OPTIONS;
  length: keyof typeof LENGTH_OPTIONS;
  variant: keyof typeof VARIANT_OPTIONS;
}

/** */
export const useProductsStore = defineStore("products", {
  state: () => {
    return {
      selectedProduct: null as ISOPRO | null,
      products: [] as ISOPRO[],
      productsMap: new Map<ISOPRO["rowKey"], ISOPRO>(),
      filterValues: {
        type: "_none",
        loadBearingCapacity: "_none",
        // TODO: concreteCovering will be ignored for v 1.0.0 but may be relevant in future versions
        // concreteCovering: '_none',
        height: "_none",
        shearForce: "_none",
        fireProtection: "_none",
        length: "_none",
        variant: "_none"
      } as FilterValues,
      productCategoriesCalculation: {} as CategoriesList,
      variantTypes: ['HV', 'UV', 'WU', 'WO'] as VariantGroupKey[],
    };
  },
  actions: {
    /** */
    async fetchProducts (): Promise<void> {
      const API = productsAPI;
      const { data } = await API.productsControllerGetAllISOCheckProducts();
      this.products = data
        .map((p) => {
        switch (p.type) {
          case "D":
            const DType = new ISOPROProductDType(p as DTypeDTO);
            this.productsMap.set(DType.rowKey, DType);
          return DType;
          case "M":
              switch ((p as unknown as ISOPROProductHeightOffsetVariantType).variant) {
                case "HV":
                  const HVType = new ISOPROProductHeightOffsetVariantType(p as HeightOffsetTypeProductDto);
                  this.productsMap.set(HVType.rowKey, HVType);
                return HVType;
                case "UV":
                  const UVType = new ISOPROProductHeightOffsetVariantType(p as HeightOffsetTypeProductDto);
                  this.productsMap.set(UVType.rowKey, UVType);
                return UVType;
                case "WO":
                  const WOType = new ISOPROProductHeightOffsetVariantType(p as HeightOffsetTypeProductDto);
                  this.productsMap.set(WOType.rowKey, WOType);
                return WOType;
                case "WU":
                  const WUType = new ISOPROProductHeightOffsetVariantType(p as HeightOffsetTypeProductDto);
                  this.productsMap.set(WUType.rowKey, WUType);
                  return WUType;
                default:
                  const MType = new ISOPROProductMType(p as MTypeDTO);
                  this.productsMap.set(MType.rowKey, MType);
                return MType;
              }
          case "QQS":
          case "QS":
          case "QQSZ":
          case "QSZ":
            const ShortElementType = new ISOPROProductShortElementType(p as ShortElementTypeDTO);
            this.productsMap.set(ShortElementType.rowKey, ShortElementType);
          return ShortElementType;
          case "SQ":
            const SQType = new ISOPROProductSQType(p as SteelSQTypeDTO);
            this.productsMap.set(SQType.rowKey, SQType);
          return SQType;
          case "SM":
            // there is no HV variant for steel types
            switch ((p as SMVariantDTO).variant) {
              case "UV":
                const SMUVType = new ISOPROProductSMVariantType(p as SMVariantDTO);
                this.productsMap.set(SMUVType.rowKey, SMUVType);
              return SMUVType;
              case "WO":
                const SMWOType = new ISOPROProductSMVariantType(p as SMVariantDTO);
                this.productsMap.set(SMWOType.rowKey, SMWOType);
              return SMWOType;
              case "WU":
                const SMWUType = new ISOPROProductSMVariantType(p as SMVariantDTO);
                this.productsMap.set(SMWUType.rowKey, SMWUType);
              return SMWUType;
              default:
                const SMType = new ISOPROProductSMType(p as SteelSMTypeDTO);
                this.productsMap.set(SMType.rowKey, SMType);
              return SMType;
            }
          default:
            const ShearForceType = new ISOPROProductShearForceType(p as ShearForceTypeDTO);
            this.productsMap.set(ShearForceType.rowKey, ShearForceType);
            return ShearForceType;
        }
      });
    },
    resetFilterValues () {
      this.filterValues = {
        type: "_none",
        loadBearingCapacity: "_none",
        height: "_none",
        shearForce: "_none",
        fireProtection: "_none",
        length: "_none",
        variant: "_none"
      };
    },
    adaptedProductType (selectedType: AdaptedISOPROType): ISOPROType {
      switch (selectedType) {
        case "M Variant":
          return "M";
        case "SM Variant":
          return "SM";
        default:
          return selectedType;
      }
    },
    adaptedVariantTypes (selectedType: AdaptedISOPROType): VariantGroupKey[] {
      switch (selectedType) {
        case "M Variant":
          return this.variantTypes;
        case "SM Variant":
          return this.variantTypes.filter((v) => v !== 'HV');
        default:
          return ['none'];
      }
    },
    /** */
    calculateAllCategories (adaptedProductType: AdaptedISOPROType): Promise<void> {
      return new Promise(async (resolve, reject) => {
        console.time("fetching data");
        const productType = this.adaptedProductType(adaptedProductType);

        const wallStructures: WallStructure[] =
          useWallStructuresStore().wallStructures;

        this.productCategoriesCalculation = {} as CategoriesList;

        this.products
          .filter(({ type }) => productType === type)
          .forEach((product, i) => {
            wallStructures.forEach((wallStructure, i) => {
              const WSType = wallStructure.type;
              const catAThresh = useThresholdsStore().getCatAByWallStructure(
                product.height,
                WSType
              );
              if (!catAThresh)
                throw new Error(`no catA found for ${product.rowKey}`);

              const { capacity: LBC, fireProtectionType: FP, height } =
                product as ISOPRO;
              // const adaptedFP = FP === 'R90' ? 'REI120' : FP;
              const h = this.productCategoriesCalculation[height];

              const insSetup = new InstallationSetup({
                product: product as ISOPRO,
                wallStructure,
                catAThresh,
                uThresh: [0],
              });

              if (!h) {
                // @ts-ignore
                this.productCategoriesCalculation[height] = {
                  // @ts-ignore
                  [FP]: { [WSType]: { [LBC]: { [product.variant || 'none']: [insSetup] } } },
                };
              } else {
                if (!h[FP]) {
                  // @ts-ignore
                  h[FP] = { [WSType]: { [LBC]: { [product.variant || 'none']: [insSetup] } } };
                } else if (!h[FP][WSType]) {
                  // @ts-ignore
                  h[FP][WSType] = { [LBC]: { [product.variant || 'none']: [insSetup] } };
                } else if (!h[FP][WSType][LBC]) {
                  // @ts-ignore
                  h[FP][WSType][LBC] = { [product.variant || 'none']: [insSetup] };
                  // @ts-ignore
                } else if (!h[FP][WSType][LBC][product.variant || 'none']) {
                  // @ts-ignore
                  h[FP][WSType][LBC][product.variant || 'none'] = [insSetup];
                } else {
                  // @ts-ignore
                  h[FP][WSType][LBC][product.variant || 'none'].push(insSetup);
                }
              }
            });
          });

        const hasFPR90 = Object.values(this.productCategoriesCalculation).some((heightCollection) => {
          return Object.keys(heightCollection).includes('R90');
        });

        if (hasFPR90) {
          (Object.entries(this.productCategoriesCalculation)).forEach(([height, heightCollection]) => {
            if (Object.keys(heightCollection).includes('R90') && Object.keys(heightCollection).includes('REI120')) {
            const t = heightCollection;

              const mergedCollection = heightCollection['R90'];
              deepMerge(mergedCollection, heightCollection['REI120']);
              t['REI120/R90' as ISOPROFireProtection] = mergedCollection;

              // @ts-ignore
              delete t['REI120'];
              // @ts-ignore
              delete t['R90'];
              }
            });
        }

        // console.log('categories: ', this.productCategoriesCalculation);

        console.timeEnd("fetching data");
        resolve();
      });
    },
    async updateHeatFlow(payload: UpdateHeatFlowDto): Promise<void> {
      const { data } = await productsAPI.productsControllerUpdateHeatFlow({
        updateHeatFlowDto: payload,
      });

      const product = this.productsMap.get(data.rowKey);
      if (!product) return;

      Object.assign(product, { heatFlow: data.heatFlow });

      if (this.selectedProduct?.rowKey === data.rowKey) {
        Object.assign(this.selectedProduct, { heatFlow: data.heatFlow });
      }
    }
  },
});
